.viewport {
  position: fixed;
  right: var(--space-5);
  bottom: var(--space-5);

  // Выше окон и меню: тост нередко сообщает об исходе того самого действия,
  // которое запустили в окне, и оказаться под ним он не должен.
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  width: 380px;
  max-width: calc(100vw - var(--space-8));
  margin: 0;
  padding: 0;
  list-style: none;
  outline: none;
}

.toast {
  // Тон задаётся одной переменной, а не набором правил: её же берут иконка
  // и полоска времени, поэтому они не могут разъехаться.
  --toast-accent: var(--color-text-disabled);

  position: relative;
  display: flex;
  gap: var(--space-3);
  align-items: flex-start;
  padding: var(--space-3);
  overflow: hidden;
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-pop);
  outline: none;

  &[data-state='open'] {
    animation: toast-in var(--motion-base) var(--ease-spring-snappy);
  }

  &[data-state='closed'] {
    animation: toast-out var(--motion-exit-base) var(--ease-exit);
  }

  // Смахивание пальцем: пока палец ведёт — тост идёт за ним, на отпускании
  // либо возвращается, либо доезжает до края. Правила смахивания стоят после
  // правил состояния намеренно — в конце жеста выставлены оба атрибута.
  &[data-swipe='move'] {
    transform: translateX(var(--radix-toast-swipe-move-x));
  }

  &[data-swipe='cancel'] {
    transform: translateX(0);
    transition: transform var(--motion-fast) var(--ease-spring-tight);
  }

  &[data-swipe='end'] {
    animation: toast-swipe-out var(--motion-exit-base) var(--ease-exit);
  }

  &:focus-visible {
    outline: var(--focus-ring) solid var(--color-accent);
    outline-offset: 2px;
  }
}

.toast--success {
  --toast-accent: var(--color-success);
}

.toast--error {
  --toast-accent: var(--color-error);

  border-color: var(--color-error-border);
}

.toast--warning {
  --toast-accent: var(--color-warning);
}

.toast--info {
  --toast-accent: var(--color-accent);
}

.toast--loading {
  --toast-accent: var(--color-text-secondary);
}

.toast__icon {
  display: inline-flex;
  flex: none;
  margin-top: 1px;
  color: var(--toast-accent);
}

.toast__spinner {
  animation: toast-spin 800ms linear infinite;
}

.toast__text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.toast__title {
  margin: 0;
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--text-md);
  font-weight: var(--weight-medium);
  letter-spacing: var(--tracking-snug);
}

.toast__description {
  margin: 0;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  line-height: 1.45;

  // Текст ошибки от сервера приходит как есть и бывает длинным: без переноса
  // по любому месту он растягивает тост и уезжает за край экрана.
  overflow-wrap: anywhere;
}

.toast__close {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin: -2px -2px 0 0;
  padding: 0;
  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-disabled);
  cursor: pointer;
  transition:
    background var(--transition-fast),
    color var(--transition-fast);

  &:hover {
    background: var(--color-elevated);
    color: var(--color-text);
  }

  &:focus-visible {
    outline: var(--focus-ring) solid var(--color-accent);
    outline-offset: 1px;
  }
}

// Полоска оставшегося времени. Она не украшение: без неё исчезновение тоста
// выглядит случайным, а с ней видно, что время идёт — и что при наведении
// оно останавливается.
.toast__life {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 2px;
  transform-origin: left;
  background: var(--toast-accent);
  opacity: 0.4;
  animation: toast-life var(--toast-life, 4000ms) linear forwards;
}

.toast[data-paused] .toast__life {
  animation-play-state: paused;
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.97);
  }
}

@keyframes toast-out {
  to {
    opacity: 0;
    transform: translateY(6px) scale(0.98);
  }
}

@keyframes toast-swipe-out {
  to {
    opacity: 0;
    transform: translateX(calc(var(--radix-toast-swipe-end-x) + 100%));
  }
}

@keyframes toast-life {
  to {
    transform: scaleX(0);
  }
}

@keyframes toast-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (width <= 560px) {
  .viewport {
    right: var(--space-3);
    bottom: var(--space-3);
    left: var(--space-3);
    width: auto;
    max-width: none;
  }

  // Действие переносится под текст: рядом с длинной подписью на узком
  // экране кнопка сжимается до нечитаемой.
  .toast {
    flex-wrap: wrap;
  }

  .toast__text {
    flex: 1 1 60%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .toast {
    &[data-state='open'],
    &[data-state='closed'],
    &[data-swipe='end'] {
      animation: none;
    }
  }

  .toast__spinner {
    animation-duration: 2s;
  }
}
